/**
 * <hidden-featured-slider> — "Featured In" press rail (gate 2026-09-01).
 * Base (<1024): native CSS scroll-snap swipe with peek; this element only keeps
 * the nav hidden.
 * Desktop (>=1024): paged slider, 3 cards per page. Prev/next page by real card
 * geometry (NOT by clientWidth — the rail carries page-margin padding, so a
 * clientWidth jump lands ~2×padding off card alignment on every page after the
 * first) and an n/m counter tracks position. The nav stays hidden whenever
 * everything fits on one page (dev decision 2026-09-01) — so today's 3 articles
 * show no arrows — and the markup ships it hidden so a no-JS render never shows
 * dead controls.
 * Paging jumps instead of animating under prefers-reduced-motion. Stays live in
 * the theme editor (the arrows are navigation, not decoration) and recomputes
 * when blocks are added or removed there.
 */
class HiddenFeaturedSlider extends HTMLElement {
  /** @type {HTMLElement | null} */
  #rail = null;

  /** @type {HTMLElement | null} */
  #nav = null;

  /** @type {HTMLElement | null} */
  #prev = null;

  /** @type {HTMLElement | null} */
  #next = null;

  /** @type {HTMLElement | null} */
  #counter = null;

  /** @type {MediaQueryList | null} */
  #desktop = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {MutationObserver | null} */
  #mutationObserver = null;

  #onUpdate = () => this.#update();

  connectedCallback() {
    this.#rail = this.querySelector('[data-featured-rail]');
    this.#nav = this.querySelector('[data-featured-nav]');
    if (!this.#rail || !this.#nav) return;

    this.#prev = this.#nav.querySelector('[data-featured-prev]');
    this.#next = this.#nav.querySelector('[data-featured-next]');
    this.#counter = this.#nav.querySelector('[data-featured-counter]');

    this.#prev?.addEventListener('click', () => this.#page(-1));
    this.#next?.addEventListener('click', () => this.#page(1));

    this.#rail.addEventListener('scroll', this.#onUpdate, { passive: true });

    this.#desktop = window.matchMedia('(min-width: 1024px)');
    this.#desktop.addEventListener('change', this.#onUpdate);

    // scrollWidth changes without the host resizing (font swap, image load, a
    // block added in the editor), so watch both the box and the child list.
    this.#resizeObserver = new ResizeObserver(this.#onUpdate);
    this.#resizeObserver.observe(this.#rail);
    this.#mutationObserver = new MutationObserver(this.#onUpdate);
    this.#mutationObserver.observe(this.#rail, { childList: true });

    this.#update();
  }

  disconnectedCallback() {
    this.#rail?.removeEventListener('scroll', this.#onUpdate);
    this.#desktop?.removeEventListener('change', this.#onUpdate);
    this.#resizeObserver?.disconnect();
    this.#mutationObserver?.disconnect();
  }

  /** @returns {HTMLElement[]} */
  get #cards() {
    return this.#rail ? /** @type {HTMLElement[]} */ ([...this.#rail.children]) : [];
  }

  /** Visible width available to cards — the rail's content box, padding excluded. */
  get #pageWidth() {
    if (!this.#rail) return 0;
    const style = getComputedStyle(this.#rail);
    return this.#rail.clientWidth - parseFloat(style.paddingInlineStart) - parseFloat(style.paddingInlineEnd);
  }

  /** How many whole cards fit in one page — 3 at desktop, 1 (with peek) at base. */
  get #perPage() {
    const cards = this.#cards;
    if (cards.length === 0 || !this.#rail) return 1;
    const gap = parseFloat(getComputedStyle(this.#rail).columnGap) || 0;
    const stride = cards[0].offsetWidth + gap;
    if (stride <= 0) return 1;
    return Math.max(1, Math.round(this.#pageWidth / stride));
  }

  get #totalPages() {
    return Math.max(1, Math.ceil(this.#cards.length / this.#perPage));
  }

  /** Page containing the card currently nearest the rail's start edge. */
  get #currentPage() {
    if (!this.#rail) return 1;
    const railLeft = this.#rail.getBoundingClientRect().left;
    const cards = this.#cards;
    let nearest = 0;
    let best = Infinity;
    cards.forEach((card, index) => {
      const distance = Math.abs(card.getBoundingClientRect().left - railLeft);
      if (distance < best) {
        best = distance;
        nearest = index;
      }
    });
    return Math.min(this.#totalPages, Math.floor(nearest / this.#perPage) + 1);
  }

  #update() {
    if (!this.#nav) return;

    const total = this.#totalPages;
    // Nav is desktop-only AND only earns its place once there's a second page.
    if (!this.#desktop?.matches || total < 2) {
      this.#nav.hidden = true;
      return;
    }

    const current = this.#currentPage;
    this.#nav.hidden = false;
    if (this.#counter) this.#counter.textContent = `${current}/${total}`;
    this.#prev?.setAttribute('aria-disabled', String(current === 1));
    this.#next?.setAttribute('aria-disabled', String(current === total));
  }

  /** @param {number} direction -1 for prev, 1 for next */
  #page(direction) {
    if (!this.#rail) return;

    const target = this.#currentPage + direction;
    if (target < 1 || target > this.#totalPages) return;

    const card = this.#cards[(target - 1) * this.#perPage];
    if (!card) return;

    // Card-relative delta, so the leading card lands flush against the rail's
    // padding edge rather than drifting by the padding on every page.
    const style = getComputedStyle(this.#rail);
    const delta =
      card.getBoundingClientRect().left -
      this.#rail.getBoundingClientRect().left -
      parseFloat(style.paddingInlineStart);

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#rail.scrollBy({ left: delta, behavior: reduced ? 'auto' : 'smooth' });
  }
}

if (!customElements.get('hidden-featured-slider')) {
  customElements.define('hidden-featured-slider', HiddenFeaturedSlider);
}
